if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cartNotification = document.querySelector('cart-notification');
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.statusMessage = this.querySelector('[data-product-form-status]');
        this.statusMessageTimeout = null;
        this.soldOutSignup = this.querySelector('[data-sold-out-signup]');
        this.soldOutSignupForm = this.soldOutSignup?.querySelector('.sold-out-signup__form');
        this.soldOutSignupInput = this.soldOutSignup?.querySelector('.sold-out-signup__input');
        this.soldOutSignupButton = this.soldOutSignup?.querySelector('.sold-out-signup__button');
        this.soldOutSignupButtonLabel = this.soldOutSignupButton?.querySelector('span');
        this.soldOutSignupSuccess = this.soldOutSignup?.querySelector('[data-sold-out-signup-success]');
        this.soldOutSignupControls = this.soldOutSignup?.querySelector('.sold-out-signup__controls');
        this.soldOutSignupSuccessTimeout = null;
        this.soldOutSignupSuccessExitTimeout = null;
        this.soldOutSignupEmbed = this.soldOutSignup?.querySelector('.sold-out-signup__embed');
        this.soldOutSignupEmbedObserver = null;

        if (this.cartDrawer) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
        this.setupSoldOutSignup();
        this.updateSoldOutSignupVisibility();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.setStatusMessage('');

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        const sectionsToRender = new Set(['cart-icon-bubble', 'cart-live-region-text']);
        if (this.cartDrawer) {
          sectionsToRender.add('cart-drawer');
          formData.append('sections_url', window.location.pathname);
          this.cartDrawer.setActiveElement(document.activeElement);
        }
        sectionsToRender.forEach((section) => formData.append('sections', section));
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.setStatusMessage('');

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.renderCartResponse(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.renderCartResponse(response);
              });
            }
          })
          .catch((e) => {
            this.setStatusMessage('');
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cartDrawer && this.cartDrawer.classList.contains('is-empty')) this.cartDrawer.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      renderCartResponse(response) {
        this.updateSectionFromMarkup('cart-icon-bubble', response.sections?.['cart-icon-bubble']);
        this.updateSectionFromMarkup('cart-live-region-text', response.sections?.['cart-live-region-text']);
        this.updateFloatingCartCountFromResponse(response);
        this.syncFloatingCartCount();
        this.setStatusMessage(window.cartStrings.itemAdded || window.cartStrings.itemUpdated, true);
        this.renderCartDrawer(response);
      }

      renderCartDrawer(response) {
        const drawerTarget = document.getElementById('CartDrawer');
        const drawerMarkup = response.sections?.['cart-drawer'];
        if (!drawerTarget || !drawerMarkup) return;

        const drawerDocument = new DOMParser().parseFromString(drawerMarkup, 'text/html');
        const drawerSource = drawerDocument.querySelector('#CartDrawer');
        if (drawerSource) {
          drawerTarget.innerHTML = drawerSource.innerHTML;
        }
      }

      updateSectionFromMarkup(targetId, markup, selector = '.shopify-section') {
        const target = document.getElementById(targetId);
        if (!target || !markup) return;

        const sourceDocument = new DOMParser().parseFromString(markup, 'text/html');
        const sourceElement = sourceDocument.querySelector(selector);
        if (sourceElement) {
          target.innerHTML = sourceElement.innerHTML;
        }
      }

      updateFloatingCartCount(itemCount) {
        if (typeof itemCount !== 'number') return;
        document.querySelectorAll('.omnee-floating-cart__count').forEach((countElement) => {
          countElement.textContent = itemCount;
        });
      }

      updateFloatingCartCountFromResponse(response) {
        if (typeof response?.item_count === 'number') {
          this.updateFloatingCartCount(response.item_count);
          return;
        }

        const bubbleMarkup = response?.sections?.['cart-icon-bubble'];
        if (!bubbleMarkup) return;

        const bubbleDocument = new DOMParser().parseFromString(bubbleMarkup, 'text/html');
        const countText =
          bubbleDocument.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent?.trim() || '';
        const parsedCount = Number.parseInt(countText, 10);

        if (!Number.isNaN(parsedCount)) {
          this.updateFloatingCartCount(parsedCount);
        }
      }

      syncFloatingCartCount() {
        fetch(`${routes.cart_url}.js`)
          .then((response) => response.json())
          .then((cartState) => {
            if (typeof cartState?.item_count === 'number') {
              this.updateFloatingCartCount(cartState.item_count);
            }
          })
          .catch((error) => {
            console.error('Unable to sync floating cart count after add to cart.', error);
          });
      }

      setStatusMessage(message, autoDismiss = false) {
        if (!this.statusMessage) return;

        window.clearTimeout(this.statusMessageTimeout);

        if (!message) {
          this.statusMessage.textContent = '';
          this.statusMessage.hidden = true;
          this.statusMessage.classList.remove('is-visible');
          return;
        }

        this.statusMessage.hidden = false;
        this.statusMessage.textContent = message;

        requestAnimationFrame(() => {
          this.statusMessage.classList.add('is-visible');
        });

        if (!autoDismiss) return;

        this.statusMessageTimeout = window.setTimeout(() => {
          this.statusMessage.classList.remove('is-visible');
          window.setTimeout(() => {
            if (!this.statusMessage.classList.contains('is-visible')) {
              this.statusMessage.textContent = '';
              this.statusMessage.hidden = true;
            }
          }, 220);
        }, 3000);
      }

      toggleSubmitButton(disable = true, text, availableText) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = availableText || window.variantStrings.addToCart;
        }

        this.updateSoldOutSignupVisibility(disable && !text ? '' : text);
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }

      setupSoldOutSignup() {
        if (!this.soldOutSignup) return;

        this.soldOutSignupTrigger = this.soldOutSignup.querySelector('[data-sold-out-signup-trigger]');
        this.soldOutSignupPanel = this.soldOutSignup.querySelector('[data-sold-out-signup-panel]');
        this.soldOutSignupPanelTimeout = null;
        this.soldOutSignupInitiallyOpen = this.soldOutSignupPanel?.dataset.initiallyOpen === 'true';

        this.soldOutSignupTrigger?.addEventListener('click', () => {
          const shouldOpen = !this.soldOutSignupPanel?.classList.contains('is-open');
          this.setSoldOutSignupPanelOpen(shouldOpen);
        });

        ['mouseenter', 'focus'].forEach((eventName) => {
          this.soldOutSignupButton?.addEventListener(eventName, () => this.updateSoldOutSignupButtonText(true));
        });

        ['mouseleave', 'blur'].forEach((eventName) => {
          this.soldOutSignupButton?.addEventListener(eventName, () => this.updateSoldOutSignupButtonText(false));
        });

        if (this.soldOutSignupInitiallyOpen) {
          this.soldOutSignupPanel.hidden = false;
          this.soldOutSignupPanel.classList.add('is-open');
          this.soldOutSignupTrigger?.setAttribute('aria-expanded', 'true');
        }

        this.initializeSoldOutSignupEmbedAccessibility();
      }

      initializeSoldOutSignupEmbedAccessibility() {
        if (!this.soldOutSignupEmbed) return;

        this.applySoldOutSignupEmbedTitles();

        this.soldOutSignupEmbedObserver?.disconnect();
        this.soldOutSignupEmbedObserver = new MutationObserver(() => this.applySoldOutSignupEmbedTitles());
        this.soldOutSignupEmbedObserver.observe(this.soldOutSignupEmbed, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['title'],
        });
      }

      applySoldOutSignupEmbedTitles() {
        if (!this.soldOutSignupEmbed) return;

        const productTitle =
          this.closest('product-info')?.querySelector('.product__title h1, .product__title .h1')?.textContent?.trim() ||
          document.title.split('–')[0].trim() ||
          'Product';
        const iframeTitle = `${productTitle} email signup`;

        this.soldOutSignupEmbed.querySelectorAll('iframe:not([title]), iframe[title=""]').forEach((iframe) => {
          iframe.setAttribute('title', iframeTitle);
        });
      }

      updateSoldOutSignupButtonText(useHoverText) {
        if (!this.soldOutSignupButton || !this.soldOutSignupButtonLabel) return;

        const nextText = useHoverText
          ? this.soldOutSignupButton.dataset.hoverText
          : this.soldOutSignupButton.dataset.defaultText;

        if (nextText) this.soldOutSignupButtonLabel.textContent = nextText;
      }

      updateSoldOutSignupVisibility(text = this.submitButtonText?.textContent) {
        if (!this.soldOutSignup || !this.submitButton) return;

        const triggerLabels = (this.dataset.notifyTriggerLabels || '')
          .split('||')
          .map((label) => label.trim())
          .filter(Boolean);
        const buttonText = (text || this.submitButtonText?.textContent || '').trim();
        const isDisabled = this.submitButton.hasAttribute('disabled');
        const shouldShow = isDisabled && triggerLabels.includes(buttonText);

        this.soldOutSignup.hidden = !shouldShow;

        if (!shouldShow) {
          this.resetSoldOutSignupState();
          this.setSoldOutSignupPanelOpen(false, true);
        } else {
          this.soldOutSignupTrigger?.setAttribute(
            'aria-expanded',
            this.soldOutSignupPanel?.classList.contains('is-open') ? 'true' : 'false'
          );
        }
      }

      resetSoldOutSignupState() {
        window.clearTimeout(this.soldOutSignupSuccessTimeout);
        window.clearTimeout(this.soldOutSignupSuccessExitTimeout);
        window.clearTimeout(this.soldOutSignupPanelTimeout);
        this.soldOutSignupInput?.setCustomValidity('');
        this.soldOutSignupForm?.reset();
        this.soldOutSignupControls?.removeAttribute('hidden');
        if (this.soldOutSignupSuccess) {
          this.soldOutSignupSuccess.hidden = true;
          this.soldOutSignupSuccess.classList.remove('is-visible', 'is-exiting');
        }
        this.updateSoldOutSignupButtonText(false);
      }

      setSoldOutSignupPanelOpen(isOpen, immediate = false) {
        if (!this.soldOutSignupPanel) return;

        window.clearTimeout(this.soldOutSignupPanelTimeout);
        this.soldOutSignupTrigger?.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

        if (isOpen) {
          this.soldOutSignupPanel.hidden = false;
          requestAnimationFrame(() => {
            this.soldOutSignupPanel.classList.add('is-open');
          });
          return;
        }

        this.soldOutSignupPanel.classList.remove('is-open');
        if (immediate) {
          this.soldOutSignupPanel.hidden = true;
          return;
        }

        this.soldOutSignupPanelTimeout = window.setTimeout(() => {
          this.soldOutSignupPanel.hidden = true;
        }, 220);
      }
    }
  );
}
